import React, { useState, useEffect } from 'react';
import { Plus, Megaphone, Edit, Trash2, Eye } from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Table, Modal, Badge, ConfirmDialog } from '../components/ui';
import { comunicadosService } from '../services/api';
import { formatDateTime } from '../utils/formatters';
import { TIPOS_COMUNICADO, PRIORIDADES } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const ComunicadosPage = () => {
  const { hasPermission } = useAuth();
  const [comunicados, setComunicados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    titulo: '', contenido: '', tipo: 'AVISO', prioridad: 'MEDIA', fecha_vigencia: ''
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await comunicadosService.getAll();
      setComunicados(response.data.data || []);
    } catch (error) {
      toast.error('Error al cargar comunicados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selected && !viewOpen) {
        await comunicadosService.update(selected.id, form);
        toast.success('Comunicado actualizado');
      } else {
        await comunicadosService.create(form);
        toast.success('Comunicado creado');
      }
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al guardar');
    }
  };

  const handleView = async (com) => {
    try {
      const response = await comunicadosService.getById(com.id);
      setSelected(response.data.data);
      setViewOpen(true);
    } catch (error) {
      toast.error('Error al cargar comunicado');
    }
  };

  const handleEdit = (com) => {
    setSelected(com);
    setForm({
      titulo: com.titulo, contenido: com.contenido, tipo: com.tipo,
      prioridad: com.prioridad, fecha_vigencia: com.fecha_vigencia?.split('T')[0] || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await comunicadosService.delete(selected.id);
      toast.success('Comunicado eliminado');
      setConfirmOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setSelected(null);
    setForm({ titulo: '', contenido: '', tipo: 'AVISO', prioridad: 'MEDIA', fecha_vigencia: '' });
  };

  const tipoVariant = { AVISO: 'warning', CONVOCATORIA: 'info', INFORMATIVO: 'success', SOCIAL: 'purple', URGENTE: 'danger' };
  const prioridadVariant = { ALTA: 'danger', MEDIA: 'warning', BAJA: 'default' };

  const columns = [
    { header: 'Título', accessor: 'titulo' },
    { header: 'Tipo', render: (row) => <Badge variant={tipoVariant[row.tipo]}>{row.tipo}</Badge> },
    { header: 'Prioridad', render: (row) => <Badge variant={prioridadVariant[row.prioridad]} size="sm">{row.prioridad}</Badge> },
    { header: 'Fecha', render: (row) => formatDateTime(row.fecha_publicacion) },
    { header: 'Vistas', render: (row) => row.vistas || 0 },
    { header: 'Acciones', render: (row) => (
      <div className="flex gap-2">
        <button onClick={() => handleView(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-blue-400"><Eye size={16} /></button>
        {hasPermission('PRESIDENTE', 'TESORERO', 'SECRETARIO') && (
          <>
            <button onClick={() => handleEdit(row)} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"><Edit size={16} /></button>
            <button onClick={() => { setSelected(row); setConfirmOpen(true); }} className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"><Trash2 size={16} /></button>
          </>
        )}
      </div>
    )}
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Comunicados</h1>
          <p className="text-slate-400">Avisos y comunicaciones para residentes</p>
        </div>
        {hasPermission('PRESIDENTE', 'TESORERO', 'SECRETARIO') && (
          <Button icon={Plus} onClick={() => { resetForm(); setModalOpen(true); }}>Nuevo Comunicado</Button>
        )}
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20"><Megaphone className="text-amber-400" size={20} /></div>
          <div>
            <p className="text-sm text-slate-400">Comunicados Activos</p>
            <p className="text-xl font-bold text-white">{comunicados.filter(c => c.estado === 'ACTIVO').length}</p>
          </div>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <Table columns={columns} data={comunicados} loading={loading} />
      </Card>

      {/* Modal Form */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selected ? 'Editar Comunicado' : 'Nuevo Comunicado'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Título" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
          <Textarea label="Contenido" value={form.contenido} onChange={(e) => setForm({ ...form, contenido: e.target.value })} rows={5} required />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select label="Tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })} options={TIPOS_COMUNICADO} required />
            <Select label="Prioridad" value={form.prioridad} onChange={(e) => setForm({ ...form, prioridad: e.target.value })} options={PRIORIDADES} />
            <Input label="Vigente Hasta" type="date" value={form.fecha_vigencia} onChange={(e) => setForm({ ...form, fecha_vigencia: e.target.value })} />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button type="submit">{selected ? 'Actualizar' : 'Publicar'}</Button>
          </div>
        </form>
      </Modal>

      {/* Modal View */}
      <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} title={selected?.titulo} size="lg">
        {selected && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Badge variant={tipoVariant[selected.tipo]}>{selected.tipo}</Badge>
              <Badge variant={prioridadVariant[selected.prioridad]}>{selected.prioridad}</Badge>
            </div>
            <div className="prose prose-invert max-w-none">
              <p className="text-slate-300 whitespace-pre-wrap">{selected.contenido}</p>
            </div>
            <div className="text-sm text-slate-500">
              Publicado: {formatDateTime(selected.fecha_publicacion)} | Vistas: {selected.vistas}
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog isOpen={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Comunicado" />
    </div>
  );
};

export default ComunicadosPage;